import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { companyService } from '../services/companyService';
import { useProgress } from '../components/TopProgressBar';

interface Company {
  configId: string;
  nombreEmpresa: string;
  estado: 'active' | 'inactive';
  agentActive: boolean;
  fechaCreacion: number;
}

// ── Tarjeta de estadística ────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: number | string;
  sub?: string;
  icon: React.ReactNode;
  accent: string;      // color del número
  iconBg: string;      // color del contenedor del ícono
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, icon, accent, iconBg }) => (
  <div className="bg-brand-surface rounded-2xl p-3 md:p-5 border border-brand-border hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] md:text-xs font-semibold text-brand-mid uppercase tracking-wide mb-1.5 md:mb-2 leading-tight">{label}</p>
        <p className={`text-2xl md:text-3xl font-extrabold ${accent}`}>{value}</p>
        {sub && <p className="text-[10px] md:text-xs text-brand-mid mt-1">{sub}</p>}
      </div>
      <div className={`w-8 h-8 md:w-11 md:h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        {icon}
      </div>
    </div>
  </div>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const { user: storeUser, canAccessCompany } = useUserStore();
  const { showLoader, hideLoader } = useProgress();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  // Loader inmediato al montar
  useLayoutEffect(() => {
    showLoader('Cargando dashboard...');
    return () => hideLoader();
  }, []); // eslint-disable-line

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCompanies();
      // Filtrar por empresas asignadas cuando el rol NO es administrador
      let filtered = data as any[];
      if (storeUser && storeUser.role !== 'administrador') {
        filtered = (data as any[]).filter(c => canAccessCompany(c.configId));
      }
      setCompanies(filtered);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const totalCompanies    = companies.length;
  const activeCompanies   = companies.filter(c => c.estado === 'active').length;
  const inactiveCompanies = companies.filter(c => c.estado === 'inactive').length;
  const agentsActive      = companies.filter(c => c.agentActive).length;
  const agentsInactive    = companies.filter(c => !c.agentActive).length;

  const thisMonth = new Date();
  const companiesThisMonth = companies.filter(c => {
    const d = new Date(c.fechaCreacion);
    return d.getMonth() === thisMonth.getMonth() && d.getFullYear() === thisMonth.getFullYear();
  }).length;

  return (
    <div className="p-4 md:p-8 bg-brand-bg min-h-full overflow-y-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-extrabold text-brand-dark">Dashboard</h1>
        <p className="text-brand-mid text-sm mt-1">
          Bienvenido, <span className="font-semibold text-brand-dark">{user?.name}</span>
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <StatCard
          label="Total empresas"
          value={totalCompanies}
          icon={
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          accent="text-brand-dark"
          iconBg="bg-brand-mid"
        />
        <StatCard
          label="Empresas activas"
          value={activeCompanies}
          sub={`${inactiveCompanies} inactivas`}
          icon={
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          accent="text-green-600"
          iconBg="bg-green-500"
        />
        <StatCard
          label="Agentes activos"
          value={agentsActive}
          sub={`${agentsInactive} inactivos`}
          icon={
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
            </svg>
          }
          accent="text-brand-mid"
          iconBg="bg-brand-mid"
        />
        <StatCard
          label="Nuevas este mes"
          value={companiesThisMonth}
          sub={thisMonth.toLocaleString('es-ES', { month: 'long', year: 'numeric' })}
          icon={
            <svg className="w-5 h-5 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
          accent="text-yellow-600"
          iconBg="bg-brand-yellow"
        />
      </div>

      {/* Empresas */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-brand-dark">Empresas registradas</h2>
          <button
            onClick={loadCompanies}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs font-semibold text-brand-mid hover:text-brand-dark transition disabled:opacity-50"
          >
            <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualizar
          </button>
        </div>

        {loading ? (
          <div className="bg-brand-surface rounded-2xl border border-brand-border p-10 text-center text-brand-mid text-sm">
            Cargando empresas...
          </div>
        ) : companies.length === 0 ? (
          <div className="bg-brand-surface rounded-2xl border border-brand-border p-14 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-brand-light flex items-center justify-center">
              <svg className="w-7 h-7 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
              </svg>
            </div>
            <p className="text-brand-dark font-semibold text-sm">No hay empresas registradas</p>
            <p className="text-brand-mid text-xs mt-1">Las empresas aparecerán aquí cuando se registren</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {companies.map((company) => (
              <div
                key={company.configId}
                className="bg-brand-surface rounded-2xl border border-brand-border p-5 hover:shadow-md hover:border-brand-yellow/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="avatar-initial w-11 h-11 rounded-xl bg-brand-dark flex items-center justify-center text-brand-yellow font-extrabold text-lg flex-shrink-0">
                    {company.nombreEmpresa.charAt(0).toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-brand-dark text-sm truncate">{company.nombreEmpresa}</h3>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        company.estado === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${company.estado === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {company.estado === 'active' ? 'Activa' : 'Inactiva'}
                      </span>

                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        company.agentActive
                          ? 'bg-brand-yellow/20 text-brand-dark'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${company.agentActive ? 'bg-brand-yellow' : 'bg-gray-400'}`} />
                        {company.agentActive ? 'Agente on' : 'Agente off'}
                      </span>
                    </div>

                    <div className="mt-2 space-y-0.5">
                      <p className="text-[10px] text-brand-mid font-mono truncate">{company.configId}</p>
                      <p className="text-[10px] text-brand-mid">
                        Creada: {new Date(company.fechaCreacion).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
